import styled, { keyframes } from "styled-components";
import { useCursors, useSocket, useSessionLabels } from "../../context/SocketContext";
import { useEffect, useRef, useState } from "react";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const OverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 50;
  overflow: hidden;
`;

const CursorWrapper = styled.div`
  position: absolute;
  transition: left 0.1s linear, top 0.1s linear;
  animation: ${fadeIn} 0.2s ease;
`;

const CursorSvg = styled.svg`
  width: 16px;
  height: 20px;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));
`;

const CursorLabel = styled.div`
  position: absolute;
  top: 18px;
  left: 12px;
  background-color: ${props => props.$color || '#6b7280'};
  color: white;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
  line-height: 1.3;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
`;

// 시야 밖 커서를 알려주는 가장자리 인디케이터 — 클릭하면 그 위치로 스크롤
const EdgeIndicator = styled.button`
  position: absolute;
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: ${props => props.$color || '#6b7280'};
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border: none;
  border-radius: 12px;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0,0,0,0.25);
  cursor: pointer;
  pointer-events: auto;
  animation: ${fadeIn} 0.2s ease;
  transition: left 0.1s linear, top 0.1s linear, transform 0.1s ease;

  &:hover {
    transform: scale(1.08);
  }
`;

// 커서 5초 안 움직이면 페이드아웃
const CURSOR_STALE_MS = 5000;

// 콘텐츠 좌표계 컨테이너(ChartContent/TimelineCanvas) 내부에 렌더된다.
// props:
//  - view: 'classic' | 'wbs' — 같은 뷰에서 보낸 커서만 표시 (좌표계가 다르므로)
//  - dayToX: (day) => x — 날짜(UTC 일수)를 이 뷰의 x 픽셀로 환산
//  - rowToY: (rowId, oy) => y|null — 행 ID를 이 뷰의 y 픽셀로 환산 (정밀 행 매핑, 선택)
//  - scrollRef: 스크롤 컨테이너 ref — 상대 커서가 내 시야 밖이면(예: 나는 10월,
//    상대는 5월) 가장자리에 "◀ 이름" 인디케이터를 띄우고 클릭 시 그 위치로 스크롤
export default function CursorOverlay({ view, dayToX, rowToY, scrollRef }) {
  const remoteCursors = useCursors();
  const { socketId } = useSocket();
  const labels = useSessionLabels();
  const [visibleCursors, setVisibleCursors] = useState({});
  const [, setScrollTick] = useState(0);
  const containerRef = useRef(null);

  // 일정 시간 안 움직인 커서 제거 (변화 없으면 기존 객체 유지해 리렌더 방지)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setVisibleCursors(prev => {
        const next = {};
        let changed = false;
        for (const [id, cursor] of Object.entries(prev)) {
          if (now - cursor.timestamp < CURSOR_STALE_MS) {
            next[id] = cursor;
          } else {
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // remoteCursors 변경 시 visibleCursors 업데이트
  useEffect(() => {
    setVisibleCursors(remoteCursors);
  }, [remoteCursors]);

  // 내가 스크롤하면 시야가 바뀌므로 인디케이터 위치 재계산
  useEffect(() => {
    const el = scrollRef?.current;
    if (!el) return;
    const onScroll = () => setScrollTick(t => t + 1);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollRef]);

  const cursorEntries = Object.entries(visibleCursors).filter(([key, cursor]) => {
    // 지금 이 탭(내 소켓)의 커서만 제외 — 내 계정의 다른 브라우저 커서는 표시
    if (socketId && (cursor.socketId === socketId || key === socketId)) return false;
    // 다른 뷰(클래식/WBS)에서 보낸 커서는 좌표계가 달라 여기선 표시하지 않음 (전역 레이어가 폴백 표시)
    if (cursor.view && view && cursor.view !== view) return false;
    // 위치 환산 불가능하면 제외
    if (cursor.day != null) return typeof dayToX === 'function';
    return cursor.x != null;
  });

  if (cursorEntries.length === 0) return null;

  // 내 시야(뷰포트) 범위 — 콘텐츠 좌표계 기준
  const scroller = scrollRef?.current;
  const viewport = scroller ? {
    left: scroller.scrollLeft,
    top: scroller.scrollTop,
    right: scroller.scrollLeft + scroller.clientWidth,
    bottom: scroller.scrollTop + scroller.clientHeight,
  } : null;

  const scrollToPos = (x, y) => {
    if (!scroller) return;
    scroller.scrollTo({
      left: Math.max(0, x - scroller.clientWidth / 2),
      top: Math.max(0, y - scroller.clientHeight / 2),
      behavior: 'smooth',
    });
  };

  return (
    <OverlayContainer ref={containerRef}>
      {cursorEntries.map(([key, cursor]) => {
        // 행 ID 매핑이 가능하면 행 기준 y (접힘 상태가 달라도 정확), 아니면 원본 y
        let top = cursor.y;
        if (cursor.rowId != null && typeof rowToY === 'function') {
          const mapped = rowToY(cursor.rowId, cursor.oy || 0);
          if (mapped != null) top = mapped;
        }
        let left = cursor.day != null ? dayToX(cursor.day) : cursor.x;
        const name = labels[cursor.socketId] || cursor.userName;

        // 시야 밖이면 가장자리 인디케이터로 표시 (예: 상대는 5월, 나는 10월)
        if (viewport) {
          const MARGIN = 8;
          const outLeft = left < viewport.left;
          const outRight = left > viewport.right - 16;
          const outUp = top < viewport.top;
          const outDown = top > viewport.bottom - 20;
          if (outLeft || outRight || outUp || outDown) {
            const ix = Math.max(viewport.left + MARGIN, Math.min(left, viewport.right - 110));
            const iy = Math.max(viewport.top + MARGIN, Math.min(top, viewport.bottom - 30));
            const arrow = outLeft ? '◀' : outRight ? '▶' : outUp ? '▲' : '▼';
            const targetX = left;
            const targetY = top;
            return (
              <EdgeIndicator
                key={key}
                $color={cursor.cursorColor}
                style={{ left: ix, top: iy }}
                title={`${name}의 위치로 이동`}
                onClick={() => scrollToPos(targetX, targetY)}
              >
                {outLeft && arrow} {name} {!outLeft && arrow}
              </EdgeIndicator>
            );
          }
        } else {
          // 스크롤 컨테이너 정보가 없으면 캔버스 범위로만 클램프
          const box = containerRef.current;
          if (box) {
            left = Math.max(0, Math.min(left, box.clientWidth - 16));
            top = Math.max(0, Math.min(top, box.clientHeight - 20));
          }
        }

        return (
        <CursorWrapper
          key={key}
          style={{
            left,
            top,
          }}
        >
          <CursorSvg viewBox="0 0 16 20">
            <path
              d="M0 0 L0 16 L4.5 12 L8.5 19 L11 18 L7 11 L13 11 Z"
              fill={cursor.cursorColor || '#6b7280'}
              stroke="white"
              strokeWidth="1"
            />
          </CursorSvg>
          <CursorLabel $color={cursor.cursorColor}>
            {name}
          </CursorLabel>
        </CursorWrapper>
        );
      })}
    </OverlayContainer>
  );
}
